/**
 * Juice coordinator — wires sim events to all juice systems and drives
 * per-frame updates. This is the single integration point that main.ts uses;
 * it owns the EventDispatcher registrations + the render-frame update call.
 *
 * World→screen projection for score popups: uses the camera's projection
 * matrix with a shared Vector3 (zero per-frame allocation). Popups that
 * project outside the viewport are clamped to the edges.
 */

import { Vector3 } from "three/webgpu";
import type { PerspectiveCamera, Scene, Group } from "three/webgpu";
import type { CameraRig } from "./cameraRig";
import type { WorldState, VehicleKind } from "../sim/types";
import { TUNING } from "../sim/tuning";
import { EventDispatcher, type JuiceHandlers } from "./events";
import { JUICE } from "./juice";
import { HitstopGate } from "./hitstopGate";
import { ScreenShake } from "./screenShake";
import { FovEffects } from "./fovPunch";
import { KickLeg } from "./kickLeg";
import { Particles } from "./particles";
import { ScorePopups } from "./popups";
import { CoinFlight } from "./coinFlight";
import { SpeedLines } from "./speedLines";
import { CrashSequence } from "./crashSequence";
import { curveOffset } from "./curve";

const P = TUNING.player;
const PA = JUICE.particles;
/**
 * World height the magnet sprite is projected from — the coin's own height, so
 * the sprite peels off exactly where the disc was.
 */
const COIN_SPRITE_Y = TUNING.coins.height;

/**
 * What a coin flies TO. Implemented by the HUD (render/hud.ts); kept as a
 * structural type so the juice coordinator does not depend on the HUD class.
 */
export interface CoinSink {
  /** Centre of the coin counter, in viewport pixels. */
  coinTarget(): { x: number; y: number };
  /** One sprite has landed: tick the counter and pulse it. */
  bumpCoin(): void;
}

/**
 * Normalised wreck speed for the trail: 0 at `trailMinSpeed` (the trail cuts
 * out at or below it), 1 at `trailSpeedRef` and above. Drives BOTH the emission
 * rate and the debris ejection speed, so the trail dies away with the skid
 * instead of stopping dead. Pure — unit tested.
 */
export function trailSpeedFrac(speed: number): number {
  const span = PA.trailSpeedRef - PA.trailMinSpeed;
  if (span <= 0) return 1;
  const f = (speed - PA.trailMinSpeed) / span;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/**
 * What the phase-transition cleanup should do this frame.
 *  - "none":      nothing to clean up.
 *  - "transient": stop the transient juice only (shake/fov/legs/popups) —
 *                 the crash vignette + 3rd-person cam intentionally stay.
 *  - "full":      the above PLUS crash.reset() (crash cam off, vignette
 *                 cleared, crash cyclist hidden) and particles.reset().
 */
export type PhaseCleanupAction = "none" | "transient" | "full";

/**
 * Edge-triggered phase-transition cleanup tracker (pure — no three/DOM deps).
 *
 * The crash cleanup MUST be driven by the TRANSITION out of crashed/gameover,
 * not by observing a rendered "title" frame: the sim can go
 * gameover → title → playing between two render frames (loop.ts runs all
 * pending sim steps before the render callback, and World.step auto-starts a
 * run when a direction key is held), so a fast restart never shows a "title"
 * frame. Latching on entry to crashed/gameover and firing on the first
 * transition to ANY non-crash phase makes the cleanup frame-rate independent.
 *
 * The destination phase is deliberately NOT enumerated (it used to be title or
 * playing only). Observed live: a headless/driven session went crashed →
 * paused, which matched neither arm, so the latch was never consumed and the
 * 3rd-person crash camera stayed pinned over ordinary gameplay for the rest of
 * the session. Any phase that is not crashed/gameover means the crash is over,
 * so any such transition is a full cleanup.
 */
export class PhaseCleanupTracker {
  private prevPhase: string;
  /** Set once we enter crashed/gameover; cleared by the full cleanup. */
  private needsCrashReset = false;

  constructor(initialPhase: string = "title") {
    this.prevPhase = initialPhase;
  }

  /** Feed the current sim phase once per render frame. */
  update(phase: string): PhaseCleanupAction {
    if (phase === this.prevPhase) return "none";
    this.prevPhase = phase;
    if (phase === "crashed") {
      this.needsCrashReset = true;
      return "none";
    }
    if (phase === "gameover") {
      this.needsCrashReset = true;
      return "transient";
    }
    if (phase === "title") {
      this.needsCrashReset = false;
      return "full";
    }
    if (this.needsCrashReset) {
      // First frame at ANY non-crash phase (playing after a restart that
      // skipped the title frame, or paused straight out of crashed).
      this.needsCrashReset = false;
      return "full";
    }
    // playing ← paused/title without a pending crash: nothing to do.
    return "none";
  }
}

/**
 * The juice coordinator. Construct after the camera, HUD root, and scene
 * exist. Call `register(dispatcher)` to wire events; call `update(state, cam, dt)`
 * each render frame.
 */
export class JuiceCoordinator {
  readonly shake: ScreenShake;
  readonly fov: FovEffects;
  readonly legs: KickLeg;
  readonly particles: Particles;
  readonly popups: ScorePopups;
  /** The magnet-suck sprites (render/coinFlight.ts). */
  readonly coinFlight: CoinFlight;
  readonly speedLines: SpeedLines;
  readonly crash: CrashSequence;

  /** Edge-triggered phase-transition cleanup (see PhaseCleanupTracker). */
  private readonly phaseCleanup = new PhaseCleanupTracker("title");
  /** Whether the hitstop FOV zoom is currently applied. */
  private hitstopActive = false;
  /** Event-armed wall-clock hitstop window (see hitstopGate.ts for why the
   *  old render-frame time-sniffing vibrated on >60 Hz displays). */
  private readonly hitstopGate = new HitstopGate();
  /** Tracks the last kick target side for whiff leg selection. */
  private lastKickSide: "left" | "right" = "right";

  // Reusable temp vector for projection (zero per-frame allocation).
  private readonly tmpVec = new Vector3();

  constructor(hudRoot: HTMLDivElement, viewportW: number, viewportH: number) {
    this.shake = new ScreenShake();
    this.fov = new FovEffects();
    this.legs = new KickLeg();
    this.particles = new Particles();
    this.popups = new ScorePopups(hudRoot);
    this.coinFlight = new CoinFlight(hudRoot);
    this.speedLines = new SpeedLines(hudRoot);
    this.crash = new CrashSequence(hudRoot);
    this.popupsViewportW = viewportW;
    this.popupsViewportH = viewportH;
  }


  /** Load async assets (the leg GLB). Call before starting the loop. */
  async load(): Promise<void> {
    await this.legs.load();
  }

  /** Attach legs to the camera (call after load + camera exists). */
  attachLegs(camera: PerspectiveCamera): void {
    this.legs.attach(camera);
  }
  /** Wire the 3rd-person crash cinematic (call after assets load + rig +
   *  scene exist). Passes registry.cyclist if available; otherwise the crash
   *  sequence loads cyclist.glb itself (one cached fetch). */
  async attachCinematic(scene: Scene, rig: CameraRig, cyclistGlb: Group | null): Promise<void> {
    await this.crash.attachCinematic(scene, rig, cyclistGlb);
  }

  /** Register all juice handlers on the dispatcher. */
  register(dispatcher: EventDispatcher): void {
    const handlers: JuiceHandlers = {
      onKick: (vehicleId, leg, contactX, contactZ, double, aimX, aimZ) => {
        this.lastKickSide = leg;
        // Shake + FOV punch + hitstop zoom window + leg poke. The kick-connect
        // event is the same trigger that makes the sim freeze (hitstop), so
        // arming the gate here mirrors the freeze exactly in wall time.
        this.shake.triggerKick();
        this.fov.triggerKickPunch();
        this.hitstopGate.trigger();
        // Particles at the contact point, the score popup AND the leg poke all
        // need the sim state: the first two for the render-only curve offset
        // (and the popup for the vehicle kind), the leg because it aims at the
        // contact RELATIVE TO THE PLAYER and the event carries an absolute x.
        // All three are therefore deferred to update(), which runs later in the
        // same frame.
        // DOUBLE KICK: the dispatcher calls this handler once per vehicle, each
        // with its OWN leg + contact point, so both leg instances poke and both
        // contacts get their dust/spark burst. The banner popup is spawned once
        // in update(), from the two queued contacts.
        this.pendingKicks.push({
          vehicleId,
          leg,
          contactX,
          contactZ,
          // Centre of mass = the leg's aim point; the contact point stays the
          // burst/popup anchor. Older events without it fall back to contact.
          aimX: aimX ?? contactX,
          aimZ: aimZ ?? contactZ,
          double: double === true,
        });
      },
      onKickWhiff: () => {
        // Alternate or use last side.
        const leg: "left" | "right" =
          this.lastKickSide === "left" ? "right" : "left";
        this.legs.whiff(leg);
        this.lastKickSide = leg;
      },
      onNearMiss: (_vehicleId, _margin) => {
        // Popup spawns near upper-centre (near-miss has no contact point;
        // the vehicle just passed).
        this.pendingNearMissPopup = true;
      },
      onCrash: (vehicleId) => {
        this.shake.triggerCrash();
        // Kill any in-flight poke: the legs are parented to the camera (like
        // the cockpit + wheel that setCrashCam hides), so an active leg would
        // otherwise fill the 3rd-person crash shot for the whole retract.
        // A plain visible=false will not stick — updateLeg re-asserts it each
        // frame.
        this.legs.reset();
        // ALSO drop queued pokes (owner playtest 2026-08-03: "the leg
        // retraction is visible if I kick and crash immediately"). A kick that
        // connects on the SAME sim step as the crash is only QUEUED here
        // (pendingKicks defers to update(), which runs after this handler), so
        // reset() alone kills nothing and the poke would START under the crash
        // cam and retract across the cinematic.
        this.pendingKicks.length = 0;
        // The vignette fires immediately; the 3rd-person cinematic is
        // deferred to update() where we can read the crash vehicle's x
        // (for the launch/camera side) + the player x (impact anchor).
        this.crash.trigger();
        this.pendingCrashBurst = true;
        this.pendingCrashCinematic = vehicleId;
      },
      onKickedVehicleDown: (_vehicleId, x, z) => {
        // Deferred: the burst must be shifted by the render-only curve offset
        // at this z, which needs the player's distance from the sim state.
        this.pendingDownBursts.push({ x, z });
      },
      onCoin: (_coinId, x, z) => {
        // Deferred like every other world-space cue: the sprite launches from
        // the coin's SCREEN position, which needs the camera and the
        // render-only curve offset (hence the player's distance) — both of
        // which only exist in `update()`.
        this.pendingCoins.push({ x, z });
      },
      onKickedVehicleGroundHit: (vehicleId, x, z) => {
        // The big plume. Deferred for the same curve-offset reason.
        this.pendingGroundHitBursts.push({ x, z });
        // ...and the moment the WRECK TRAIL starts for this slot: the wreck has
        // touched down, so from here until it slows below `trailMinSpeed` it
        // pours smoke and throws debris (see updateSlideTrails).
        if (vehicleId < this.slideGrounded.length) this.slideGrounded[vehicleId] = 1;
      },
    };
    dispatcher.add(handlers);
  }

  // Deferred popup/burst data (resolved in update when we have sim state).
  /** Kick contacts awaiting the curve correction + vehicle-kind lookup (an
   *  array: a render frame can contain several sim steps, hence several kicks). */
  private readonly pendingKicks: {
    vehicleId: number;
    leg: "left" | "right";
    contactX: number;
    contactZ: number;
    /** Target vehicle's centre of mass — the leg's aim point. */
    aimX: number;
    aimZ: number;
    /** True for both halves of a DOUBLE KICK (drives the banner popup). */
    double: boolean;
  }[] = [];
  private pendingNearMissPopup = false;
  /** Coin pickups awaiting projection to screen space. */
  private readonly pendingCoins: { x: number; z: number }[] = [];
  /**
   * Where the magnet sprites fly to, and what they tick when they land: the
   * HUD's coin counter. Null until `setCoinSink` is called, in which case the
   * flight is simply skipped — the sim state (and therefore the score) is
   * unaffected either way.
   */
  private coinSink: CoinSink | null = null;
  private pendingCrashBurst = false;
  /** Kicked-vehicle-down dust bursts awaiting the curve correction. */
  private readonly pendingDownBursts: { x: number; z: number }[] = [];
  /** First-ground-impact dust plumes awaiting the curve correction. */
  private readonly pendingGroundHitBursts: { x: number; z: number }[] = [];
  // --- Slide trail state (one entry per threat pool slot, pre-sized) --------
  // The sim reports only a pose, so the wreck's speed is differentiated here
  // from the mirrored entity position between render frames.
  private readonly slidePrevX = new Float32Array(TUNING.traffic.maxThreatVehicles);
  private readonly slidePrevZ = new Float32Array(TUNING.traffic.maxThreatVehicles);
  private readonly slideSeen = new Uint8Array(TUNING.traffic.maxThreatVehicles);
  /** Seconds accumulated toward this slot's next slide puff. */
  private readonly slideAccum = new Float32Array(TUNING.traffic.maxThreatVehicles);
  /**
   * 1 once this slot's wreck has had its FIRST GROUND IMPACT (latched from the
   * `kickedVehicleGroundHit` event), 0 while it is still on the launch arc or
   * the slot is not a wreck at all. This — not a height test — is what gates
   * the trail: a wreck tumbling end over end rides on its CORNERS, so its body
   * centre sits at the box half-DIAGONAL (2.6 m for an SUV, far above its
   * 0.95 m resting height) for the whole first second of a skid. The old height
   * test read that as "still airborne" and the trail never started.
   */
  private readonly slideGrounded = new Uint8Array(TUNING.traffic.maxThreatVehicles);
  /** Deferred crash-cinematic: the crash vehicle id whose x picks the launch
   *  side; resolved in update() where the sim state is available. */
  private pendingCrashCinematic = -1;

  /**
   * Per-frame update. Call AFTER the camera rig has updated (so shake/fov
   * are applied additively on top).
   *
   * @param state  current WorldState (for vehicle lookups + phase + speed).
   * @param cam    the rig's PerspectiveCamera.
   * @param dt     render-frame delta seconds.
   */
  update(state: WorldState, cam: PerspectiveCamera, dt: number): void {
    // --- Hitstop zoom: event-armed wall-clock window (hitstopGate.ts).
    // NEVER inferred by comparing state.time between render frames — on
    // displays faster than 60 Hz most rAF frames contain no sim step, so that
    // comparison toggles the zoom on alternating frames (whole-scene FOV
    // pumping at the sim-tick frequency — the "everything vibrates" bug).
    const hitstop = this.hitstopGate.update(dt, state.phase === "playing");
    if (hitstop !== this.hitstopActive) {
      this.hitstopActive = hitstop;
      this.fov.setHitstop(hitstop);
    }

    // --- Resolve deferred popups/bursts (need sim state) ---
    // World-space juice lives in the SAME (curved) render space as the
    // vehicles/road, so every placement at a non-zero sim z must be shifted by
    // the render-only curve offset — otherwise sparks and popups drift off the
    // vehicle they belong to on a bend (up to ~0.5 m at the kick window, more
    // for the far kicked-vehicle-down bursts).
    const distance = state.player.distance;
    if (this.pendingKicks.length > 0) {
      // DOUBLE KICK banner: one per double-kick sim event, anchored midway
      // between the two contacts. Accumulated while the contacts are resolved.
      let doubleCount = 0;
      let doubleX = 0;
      let doubleZ = 0;
      for (const pk of this.pendingKicks) {
        const kind = this.findVehicleKind(state, pk.vehicleId);
        const wx = pk.contactX + curveOffset(pk.contactZ, distance);
        // AIM THE LEG at the vehicle's CENTRE OF MASS, in the player's frame
        // (the legs are camera children and the rig tracks the player's x).
        // The contact point still drives the extension scale and the burst.
        this.legs.kick(
          pk.leg,
          pk.contactX - state.player.x,
          pk.contactZ,
          pk.aimX - state.player.x,
          pk.aimZ,
        );
        // Dust + sparks AT THE CONTACT POINT, at mid-body height.
        this.particles.burstKick(wx, PA.kickContactY, pk.contactZ);
        const screen = this.project(cam, wx, PA.kickContactY, pk.contactZ, state);
        if (screen) {
          this.popups.spawnKick(kind, screen.x, screen.y);
        }
        if (pk.double) {
          doubleCount++;
          doubleX += wx;
          doubleZ += pk.contactZ;
        }
      }
      this.pendingKicks.length = 0;
      if (doubleCount > 0) {
        const banner = this.project(
          cam,
          doubleX / doubleCount,
          PA.kickContactY + 1.2,
          doubleZ / doubleCount,
          state,
        );
        if (banner) this.popups.spawnDoubleKick(banner.x, banner.y);
      }
    }
    if (this.pendingGroundHitBursts.length > 0) {
      for (const g of this.pendingGroundHitBursts) {
        this.particles.burstGroundHit(g.x + curveOffset(g.z, distance), g.z);
      }
      this.pendingGroundHitBursts.length = 0;
    }
    if (this.pendingDownBursts.length > 0) {
      for (const d of this.pendingDownBursts) {
        this.particles.burstDown(d.x + curveOffset(d.z, distance), d.z);
      }
      this.pendingDownBursts.length = 0;
    }
    this.updateSlideTrails(state, dt, distance);
    if (this.pendingNearMissPopup) {
      this.pendingNearMissPopup = false;
      // Near-miss popup: near the top-centre of the viewport.
      const vw = this.popupsViewportW;
      this.popups.spawnNearMiss(vw * 0.5, this.popupsViewportH * 0.3);
    }
    if (this.pendingCoins.length > 0) {
      const sink = this.coinSink;
      if (sink !== null) {
        const target = sink.coinTarget();
        for (const c of this.pendingCoins) {
          const screen = this.project(
            cam,
            c.x + curveOffset(c.z, distance),
            COIN_SPRITE_Y,
            c.z,
            state,
          );
          if (screen) {
            this.coinFlight.spawn(screen.x, screen.y, target.x, target.y);
          } else {
            // Behind the camera (a pickup right on the player plane on a
            // hard bend): no sensible launch point, so credit the counter
            // immediately rather than swallowing the tick.
            sink.bumpCoin();
          }
        }
      }
      this.pendingCoins.length = 0;
    }
    if (this.pendingCrashBurst) {
      this.pendingCrashBurst = false;
      this.particles.burstCrash(state.player.x, P.cameraHeight, 0);
    }
    if (this.pendingCrashCinematic >= 0) {
      const vid = this.pendingCrashCinematic;
      this.pendingCrashCinematic = -1;
      // Find the crash vehicle's lateral offset to pick the launch/camera side.
      let vehX = state.player.x;
      const vehicles = state.vehicles;
      for (let i = 0; i < vehicles.length; i++) {
        if (vehicles[i].id === vid) { vehX = vehicles[i].x; break; }
      }
      this.crash.startCinematic(vehX - state.player.x, state.player.x);
    }
    // --- Crash cinematic camera pose (must run BEFORE shake/fov so they
    //     layer additively on top of the 3rd-person crash cam, not under it) ---
    this.crash.update(dt, state);

    // --- Apply shake + FOV to the camera (additive, after rig.update) ---
    this.shake.update(dt);
    if (this.shake.active) {
      this.shake.apply(cam);
    }

    // Camera dip from kick leg connect.
    const dip = this.legs.getDipRad();
    if (dip !== 0) {
      cam.rotation.x += dip;
    }

    this.fov.apply(cam, dt);

    // --- Update subsystems ---
    this.legs.update(dt);
    // The camera drives the particle billboards (every quad faces the viewer).
    this.particles.update(dt, cam);
    this.popups.update(dt);
    this.coinFlight.update(dt);
    this.speedLines.update(state.player.forwardSpeed, dt);

    // --- Phase transition cleanup (edge-triggered — see PhaseCleanupTracker) ---
    const action = this.phaseCleanup.update(state.phase);
    if (action !== "none") {
      // "transient" (→ gameover): the crash cinematic is over, so stop the
      // transient juice (shake, FOV punch, legs, popups) and let the game-over
      // screen sit still. The crash vignette + 3rd-person camera stay.
      this.shake.reset();
      this.fov.reset();
      this.legs.reset();
      this.popups.reset();
      this.coinFlight.reset();
      this.pendingCoins.length = 0;
      this.speedLines.reset();
      this.hitstopGate.reset();
      this.hitstopActive = false;
      if (action === "full") {
        // Restart (→ title, or straight to playing when the restart skipped a
        // rendered title frame): also drop the crash cam, vignette + cyclist.
        this.particles.reset();
        this.crash.reset();
        // Drop the slide-trail history so the new run's first kicked-vehicle
        // frame is treated as a fresh sample (a stale prev position across a
        // reset would read as a huge one-frame speed).
        this.slideSeen.fill(0);
        this.slideAccum.fill(0);
        this.slideGrounded.fill(0);
      }
    }
  }

  /** Viewport dimensions for popup placement (updated via setViewportSize). */
  private popupsViewportW = 0;
  private popupsViewportH = 0;

  /**
   * Wire the coin counter. Called once from main.ts with the HUD, which both
   * supplies the flight target and receives the per-arrival tick.
   */
  setCoinSink(sink: CoinSink): void {
    this.coinSink = sink;
    this.coinFlight.setArrivalHandler(() => sink.bumpCoin());
  }

  setViewportSize(w: number, h: number): void {
    this.popupsViewportW = w;
    this.popupsViewportH = h;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /**
   * WRECK TRAIL: a wreck skidding along the ground keeps pouring smoke and
   * throwing debris after the impact plume, until it has nearly stopped.
   *
   * There is no sim event for "still sliding" and there must not be one — a
   * per-frame event per wreck would flood the sim event queue. Instead this
   * scans the mirrored kicked entities each frame (they carry the live Rapier
   * pose, see world.syncKickedBodies) and differentiates their horizontal
   * position between frames to recover the wreck's ground speed. No sim
   * plumbing is required beyond the mirroring that already exists.
   *
   * A slot emits once it has TOUCHED DOWN (`slideGrounded`, latched from the
   * one-shot `kickedVehicleGroundHit` event — NOT a height test, see that
   * field) and while it is FASTER than `trailMinSpeed`. The rate scales with
   * that speed, from
   * `trailRateMin` at the threshold to `trailRateMax` at `trailSpeedRef`, so a
   * fresh 34 m/s skid smokes hard and the trail thins out as the wreck dies —
   * and it is capped at `trailMaxPerFrame` emissions across all wrecks in a
   * frame, so a long multi-wreck skid can never drain the particle pool out
   * from under the impact plumes.
   *
   * Zero allocation: all per-slot state lives in pre-sized typed arrays keyed
   * by the vehicle's stable pool-slot id.
   */
  private updateSlideTrails(state: WorldState, dt: number, distance: number): void {
    const vehicles = state.vehicles;
    const maxThreat = TUNING.traffic.maxThreatVehicles;
    let emitted = 0;
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      const id = v.id;
      if (id >= maxThreat) continue; // ambients are never kicked
      if (v.motion !== "kicked") {
        this.slideSeen[id] = 0;
        this.slideAccum[id] = 0;
        this.slideGrounded[id] = 0;
        continue;
      }
      const prevSeen = this.slideSeen[id];
      const dx = v.x - this.slidePrevX[id];
      const dz = v.z - this.slidePrevZ[id];
      this.slidePrevX[id] = v.x;
      this.slidePrevZ[id] = v.z;
      this.slideSeen[id] = 1;
      if (!prevSeen || dt <= 0) continue; // no previous sample → no speed yet

      const speed = Math.hypot(dx, dz) / dt;
      if (!this.slideGrounded[id] || speed <= PA.trailMinSpeed) {
        this.slideAccum[id] = 0;
        continue;
      }
      // Rate scales with the wreck's live speed (emissions per second).
      const frac = trailSpeedFrac(speed);
      const rate = PA.trailRateMin + (PA.trailRateMax - PA.trailRateMin) * frac;
      this.slideAccum[id] += dt;
      if (this.slideAccum[id] < 1 / rate) continue;
      this.slideAccum[id] = 0;
      if (emitted >= PA.trailMaxPerFrame) continue;
      emitted++;
      this.particles.puffWreckTrail(
        v.x + curveOffset(v.z, distance),
        v.z,
        v.y,
        frac,
      );
    }
  }

  /** Find a vehicle's kind by id in the sim state. */
  private findVehicleKind(state: WorldState, vehicleId: number): VehicleKind {
    const vehicles = state.vehicles;
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      if (v.id === vehicleId) return v.kind;
    }
    return "car"; // fallback (shouldn't happen for a fresh kick)
  }

  /**
   * Project a world-space point to screen-space pixels within the 9:16
   * viewport. Returns null if behind the camera.
   */
  private project(
    cam: PerspectiveCamera,
    wx: number,
    wy: number,
    wz: number,
    _state: WorldState,
  ): { x: number; y: number } | null {
    const v = this.tmpVec.set(wx, wy, wz);
    v.project(cam);
    // v is now in NDC [-1, 1]. Convert to viewport pixels.
    if (v.z > 1) return null; // behind camera
    const px = (v.x * 0.5 + 0.5) * this.popupsViewportW;
    const py = (-v.y * 0.5 + 0.5) * this.popupsViewportH;
    // Clamp to viewport edges with a margin.
    const margin = 30;
    return {
      x: Math.max(margin, Math.min(this.popupsViewportW - margin, px)),
      y: Math.max(margin, Math.min(this.popupsViewportH - margin, py)),
    };
  }
}

// Re-export for convenience (so main.ts can import from one place if desired).
export { EventDispatcher } from "./events";
export { JUICE } from "./juice";
